import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Auth.css';
import { API_URL } from './config/apiConfig';
import { useContainerScale } from './hooks/useViewportScale';

function ConfirmEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();

  useEffect(() => {
    const confirmEmail = async () => {
      try {
        const apiUrl = API_URL;
        const response = await fetch(`${apiUrl}/auth/confirm-email/${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        // Handle network errors or non-JSON responses
        if (!response.ok && response.status === 503) {
          setStatus('error');
          setMessage(t('confirmEmail.serviceUnavailable') || 'Service temporarily unavailable. Please try again later.');
          return;
        }

        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          // If response is not JSON, it's likely a server error
          setStatus('error');
          setMessage(t('confirmEmail.error') || 'Failed to confirm email. Please try again.');
          return;
        }

        // Handle success (including already confirmed)
        if (response.ok || (data.success || data.alreadyConfirmed)) {
          setStatus('success');
          // Use appropriate message for already confirmed vs newly confirmed
          if (data.alreadyConfirmed) {
            setMessage(t('confirmEmail.alreadyConfirmed') || 'Your email address has already been confirmed.');
          } else {
            setMessage(data.message || t('confirmEmail.success'));
          }
          // In Electron, close the window after showing success
          // In browser, don't redirect - just show success message
          if (window.electron && window.electron.closeWindow) {
            // Wait a bit so user can see the success message
            setTimeout(() => {
              window.electron.closeWindow();
            }, 2000);
          }
        } else {
          // Only show error/expired states for actual failures
          if (data.error === 'Confirmation token expired') {
            setStatus('expired');
            setMessage(data.message || t('confirmEmail.expired'));
          } else {
            // Only show error if it's a real error (not already confirmed)
            setStatus('error');
            setMessage(data.error || t('confirmEmail.error'));
          }
        }
      } catch (error) {
        console.error('Email confirmation error:', error);
        setStatus('error');
        setMessage(t('confirmEmail.error') || 'Failed to confirm email. Please check your connection and try again.');
      }
    };

    if (token) {
      confirmEmail();
    } else {
      setStatus('error');
      setMessage(t('confirmEmail.invalidToken'));
    }
  }, [token, navigate, t]);

  return (
    <div className="auth-viewport-wrapper">
      <div className="auth-fullscreen-background" />
      <div 
        className="auth-viewport-container"
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className={`auth-container ${isContainerMobile ? 'auth-mobile' : ''}`}>
          <div className="auth-card">
            <div className="auth-game-title">{t('welcome.gameTitle')}</div>
            {status === 'loading' && (
              <>
                <h1 className="auth-title">{t('confirmEmail.loading')}</h1>
                <div className="auth-message">
                  <p>{t('confirmEmail.verifying')}</p>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <h1 className="auth-title" style={{ color: '#90ee90' }}>{t('confirmEmail.successTitle')}</h1>
                <div className="auth-success">
                  <p>{message}</p>
                  {window.electron && window.electron.closeWindow && (
                    <p style={{ marginTop: '10px', fontSize: '14px', color: '#aaa' }}>
                      {t('confirmEmail.closingWindow') || 'This window will close automatically...'}
                    </p>
                  )}
                </div>
                {!window.electron && (
                  <div style={{ marginTop: 20, textAlign: 'center' }}>
                    <button
                      onClick={() => {
                        if (window.electron && window.electron.closeWindow) {
                          window.electron.closeWindow();
                        } else {
                          window.close();
                        }
                      }}
                      className="auth-button"
                      style={{ marginTop: '10px' }}
                    >
                      {t('confirmEmail.closeWindow') || 'Close Window'}
                    </button>
                  </div>
                )}
              </>
            )}

            {status === 'expired' && (
              <>
                <h1 className="auth-title" style={{ color: '#ffa500' }}>{t('confirmEmail.expiredTitle')}</h1>
                <div className="auth-error">
                  <p>{message}</p>
                </div>
                <div className="auth-links" style={{ marginTop: 20, flexDirection: 'row', justifyContent: 'center', gap: 15 }}>
                  <Link to="/resend-confirmation" className="auth-link">
                    {t('confirmEmail.resendEmail')}
                  </Link>
                  <Link to="/" className="auth-link">
                    {t('confirmEmail.backToLogin')}
                  </Link>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <h1 className="auth-title" style={{ color: '#ff6b6b' }}>{t('confirmEmail.errorTitle')}</h1>
                <div className="auth-error">
                  <p>{message}</p>
                </div>
                {message !== t('confirmEmail.alreadyConfirmed') && (
                  <div className="auth-links" style={{ marginTop: 20, flexDirection: 'row', justifyContent: 'center', gap: 15 }}>
                    <Link to="/resend-confirmation" className="auth-link">
                      {t('confirmEmail.resendEmail')}
                    </Link>
                    <Link to="/" className="auth-link">
                      {t('confirmEmail.backToLogin')}
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfirmEmail;

